import type { IntervalJSON } from '@highimpact-frontend/api-types';
import { resolveIntervalToFixed } from '@highimpact/time-intervals';
import dayjs, { ManipulateType } from 'dayjs';
import type { TimeRangeOption } from '../config/plotTypes';

type RelativeTimeRangeUnion = Extract<
  IntervalJSON,
  { type: 'calendar_time_range' } | { type: 'relative_time_range' }
>;

const timeUnitMap: Record<TimeRangeOption['unit'], ManipulateType> = {
  minute: 'minute',
  hour: 'hour',
  day: 'day',
  week: 'week',
  month: 'month',
  year: 'year',
};

const timeUnitToRelativeUnitMap: Record<
  TimeRangeOption['unit'],
  RelativeTimeRangeUnion['unit']
> = {
  minute: 'minutes',
  hour: 'hours',
  day: 'days',
  week: 'weeks',
  month: 'months',
  year: 'years',
};

function selectClosestToNowByEndDate(
  intervals: IntervalJSON[]
): IntervalJSON | undefined {
  if (!intervals.length) {
    return undefined;
  }

  const nowMs = Date.now();
  let selected: IntervalJSON | undefined;
  let selectedEndMs: number | undefined;
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const interval of intervals) {
    const endMs = new Date(resolveIntervalToFixed(interval).enddate).getTime();
    if (Number.isNaN(endMs)) {
      continue;
    }

    const distance = Math.abs(endMs - nowMs);
    const shouldReplace =
      distance < bestDistance ||
      (distance === bestDistance &&
        selectedEndMs !== undefined &&
        endMs > selectedEndMs);

    if (!selected || shouldReplace) {
      selected = interval;
      selectedEndMs = endMs;
      bestDistance = distance;
    }
  }

  return selected;
}

/** 
 * Find the interval closest to now by end date and modify it to the target time unit and quantity.
 * 
 * - If the interval is a relative or calendar interval, modify it to the target time unit and quantity.
 * - If the interval is a fixed interval, modify it to span the target time unit and quantity.
 * - If no interval is found, return undefined.
 * 
 * @param timeRangeOption - The target time unit and quantity to modify the interval to.
 * @param intervals - The list of intervals to select from.
 * @returns The modified interval or undefined if no interval is found.
 */
export function buildIntervalFromTimeUnit(
  timeRangeOption: TimeRangeOption,
  intervals: IntervalJSON[]
): IntervalJSON | undefined {
  const selected = selectClosestToNowByEndDate(intervals);

  if (!selected) {
    return undefined;
  }

  if (
    selected.type === 'relative_time_range' ||
    selected.type === 'calendar_time_range'
  ) {
    return {
      ...selected,
      unit: timeUnitToRelativeUnitMap[timeRangeOption.unit],
      quantity: timeRangeOption.quantity,
    };
  }

  const resolvedSelected = resolveIntervalToFixed(selected);
  const enddate = resolvedSelected.enddate;
  const startdate = dayjs(enddate)
    .subtract(timeRangeOption.quantity, timeUnitMap[timeRangeOption.unit])
    .toISOString();

  return {
    type: 'fixed_time_range',
    name: selected.name,
    startdate,
    enddate,
  };
}
